/* eslint-disable no-param-reassign */
/**
 * Cultural Intelligence slice: the Cultural Intel report of an intel report, and its per-trend
 * "Interpret for my industry" runs (BE guide CULTURAL_INTEL_ENDPOINTS.md).
 *
 * Follows the BrandSafety report slice's conventions: plain setter reducers, hand-rolled thunks that
 * return a Promise (the API client is callback-based), no createAsyncThunk. Components read
 * `state.CulturalIntelligence` directly.
 *
 * Every id here is the **cultural request id** (`report.layers.cultural.id`), not the intel report id.
 */

import { createSlice } from '@reduxjs/toolkit';
import { isApiSuccess } from '../../constants/platformStatusCodes';
import { captureMessage } from '../../util/sentry';
import {
  culturalIntelReportSchema,
  culturalIntelStatusSchema,
  interpretationSchema,
  trendDetailSchema,
} from '../../schemas/culturalIntelligence';
import {
  getCulturalIntelReport,
  getCulturalIntelStatus,
  getCulturalIntelTrend,
  getTrendInterpretation,
  postTrendInterpretation,
  retryCulturalIntelReport,
} from './culturalIntelligenceApi';
import adaptCulturalReport, { interpretationsOf } from './utils/adaptCulturalReport';
import { reportLayerStatusChanged } from '../IntelligenceReport/IntelligenceReport.slice';

/** First run, refresh and interpretation status (BE `CulturalStatus`). */
export const CULTURAL_STATUS = {
  QUEUED: 'queued',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
};

export const isInterpretationRunning = (interpretation) =>
  interpretation?.status === CULTURAL_STATUS.QUEUED ||
  interpretation?.status === CULTURAL_STATUS.PROCESSING;

const initialState = {
  // GET /cultural-intel/{id}: first run status, its error (+ retryable), refresh setting.
  status: null,
  report: null,
  reportLoading: false,
  reportError: null,
  // The first run hasn't completed yet (the report endpoint answers 404 "not ready").
  reportPending: false,
  // The cultural request id whose report sits in `report`: the cache key.
  reportFetchedId: null,
  // Trend id → { id, status } of its interpretation in the edition on screen.
  interpretations: {},
  // Trend id → why its last "Interpret" click failed (e.g. throttled), until the next click.
  interpretErrors: {},
  // Interpretation id → { loading, data, error }: GET .../interpretations/{id}.
  interpretationContent: {},
  // trendDetailKey → { responseId, loading, data, error, notFound }: GET .../trends/{trendId}, the
  // drawer's sections. A completed edition never changes, so a loaded one is kept for the life of the
  // page; a refetched report with another edition drops the rest.
  trendDetails: {},
};

/** One trend's detail in one edition of one report: the cache key (BE guide: never reuse across editions). */
export const trendDetailKey = (id, trendId, responseId) => `${id}:${trendId}:${responseId}`;

const httpStatusOf = (resp) => resp?._httpStatus ?? resp?.httpStatus; // eslint-disable-line no-underscore-dangle

/** A 422's first field error (`errors: { trendId: ['…'] }`) says more than its generic `message`. */
const fieldErrorOf = (resp) => {
  const first = resp?.errors && typeof resp.errors === 'object' && Object.values(resp.errors)[0];
  return (Array.isArray(first) ? first[0] : first) || null;
};

const messageOf = (resp, fallback) =>
  fieldErrorOf(resp) || resp?.message || resp?.error || fallback;

const CulturalIntelligenceSlice = createSlice({
  name: 'CulturalIntelligence',
  initialState,
  reducers: {
    setStatus(state, action) {
      state.status = action.payload;
    },
    setReportLoading(state, action) {
      state.reportLoading = action.payload;
    },
    setReportError(state, action) {
      state.reportError = action.payload;
    },
    setReportPending(state, action) {
      state.reportPending = action.payload;
    },
    /** A loaded edition: the adapted report and every card's interpretation. */
    setReport(state, action) {
      const { id, report, interpretations } = action.payload;
      state.report = report;
      state.reportFetchedId = id == null ? null : String(id);
      state.interpretations = interpretations;
      state.reportPending = false;
      // Another edition: its trend details are stale.
      Object.keys(state.trendDetails).forEach((key) => {
        if (state.trendDetails[key].responseId !== report.responseId)
          delete state.trendDetails[key];
      });
    },
    setTrendDetail(state, action) {
      const { key, ...entry } = action.payload;
      state.trendDetails[key] = { ...state.trendDetails[key], ...entry };
    },
    setInterpretation(state, action) {
      const { trendId, interpretation } = action.payload;
      if (interpretation) state.interpretations[trendId] = interpretation;
      else delete state.interpretations[trendId];
    },
    setInterpretError(state, action) {
      const { trendId, error } = action.payload;
      if (error) state.interpretErrors[trendId] = error;
      else delete state.interpretErrors[trendId];
    },
    setInterpretationContent(state, action) {
      const { interpretationId, ...entry } = action.payload;
      state.interpretationContent[interpretationId] = {
        ...state.interpretationContent[interpretationId],
        ...entry,
      };
    },
    /**
     * Reverb `cultural-intel.{id}` → `.status.updated`: an interpretation moved to processing,
     * completed or failed. Only the edition on screen counts.
     */
    interpretationStatusChanged(state, action) {
      const event = action.payload || {};
      if (!state.report || event.culturalIntelResponseId !== state.report.responseId) return;
      state.interpretations[event.trendId] = { id: event.interpretationId, status: event.status };
      // Content fetched while it was running is stale now.
      if (event.status === CULTURAL_STATUS.COMPLETED || event.status === CULTURAL_STATUS.FAILED) {
        delete state.interpretationContent[event.interpretationId];
      }
    },
    resetCulturalIntelligenceReport() {
      return initialState;
    },
  },
});

export const {
  setStatus,
  setReportLoading,
  setReportError,
  setReportPending,
  setReport,
  setTrendDetail,
  setInterpretation,
  setInterpretError,
  setInterpretationContent,
  interpretationStatusChanged,
  resetCulturalIntelligenceReport,
} = CulturalIntelligenceSlice.actions;

/** GET /cultural-intel/{id}: where the report stands (and the first run's error when it failed). */
export const fetchCulturalIntelStatus = ({ id }) => (dispatch) =>
  getCulturalIntelStatus(id).then((resp) => {
    if (!isApiSuccess(resp)) return null;
    const parsed = culturalIntelStatusSchema.safeParse(resp.data);
    if (!parsed.success) {
      captureMessage('Cultural Intel status failed schema validation', 'error', {
        culturalRequestId: id,
        issues: parsed.error.issues,
      });
      return null;
    }
    dispatch(setStatus(parsed.data));
    return parsed.data;
  });

/**
 * Load the latest completed edition. A cache hit on the same id serves the store; `force` (Refresh,
 * a new edition) refetches; `silent` keeps the page on show while it does (a refetch after a socket
 * event or reconnect), and a failed silent refetch keeps what's shown.
 *
 * @param {{ id: string|number, force?: boolean, silent?: boolean }} args
 */
export const fetchCulturalIntelligenceReport = ({ id, force = false, silent = false } = {}) => (
  dispatch,
  getState
) => {
  if (id == null || id === '') {
    dispatch(setReportError('Missing report id.'));
    return Promise.resolve(null);
  }

  const key = String(id);
  const current = getState().CulturalIntelligence || {};
  if (!force && current.report && current.reportFetchedId === key) {
    return Promise.resolve(current.report);
  }
  // A different report: drop the previous one's cards and interpretations.
  if (current.reportFetchedId && current.reportFetchedId !== key) {
    dispatch(resetCulturalIntelligenceReport());
  }

  if (!silent) dispatch(setReportLoading(true));
  dispatch(setReportError(null));

  return Promise.all([getCulturalIntelReport(id), dispatch(fetchCulturalIntelStatus({ id }))]).then(
    ([resp, status]) => {
      dispatch(setReportLoading(false));
      if (isApiSuccess(resp)) {
        const parsed = culturalIntelReportSchema.safeParse(resp.data);
        if (!parsed.success) {
          captureMessage('Cultural Intel report failed schema validation', 'error', {
            culturalRequestId: key,
            issues: parsed.error.issues,
          });
          if (!silent)
            dispatch(setReportError('This report is temporarily unavailable. Please try again.'));
          return null;
        }
        const report = adaptCulturalReport(parsed.data, status);
        dispatch(setReport({ id: key, report, interpretations: interpretationsOf(parsed.data) }));
        return report;
      }

      if (silent) return null;
      // 404 "The report is not ready": the first run is still going (or failed; the status says).
      if (httpStatusOf(resp) === 404 && status) {
        dispatch(setReportPending(true));
        return null;
      }
      dispatch(
        setReportError(
          httpStatusOf(resp) === 404
            ? "This report isn't available for your workspace."
            : messageOf(resp, 'Failed to load the cultural intelligence report.')
        )
      );
      return null;
    }
  );
};

/**
 * GET /cultural-intel/{id}/trends/{trendId}: the drawer's sections for one trend of the edition on
 * screen, read once per (report, trend, edition) and kept (a failure isn't, so the next open asks
 * again). A 404 means the trend isn't in that edition, i.e. the page is stale: the report is read
 * again and the entry is flagged `notFound` for the drawer to close on. A 422 can't happen unless
 * the report's edition id isn't an integer, and shows as the entry's error.
 *
 * @param {{ trendId: string }} args
 */
export const fetchTrendDetail = ({ trendId }) => (dispatch, getState) => {
  const { report, reportFetchedId: id, trendDetails } = getState().CulturalIntelligence;
  if (!report || id == null) return Promise.resolve(null);

  const { responseId } = report;
  const key = trendDetailKey(id, trendId, responseId);
  const entry = trendDetails[key];
  if (entry?.loading || entry?.data) return Promise.resolve(entry.data ?? null);

  dispatch(setTrendDetail({ key, responseId, loading: true, error: null, notFound: false }));
  return getCulturalIntelTrend(id, trendId, responseId).then((resp) => {
    // The edition changed while this was in flight (setReport dropped its entries): nothing to keep.
    if (getState().CulturalIntelligence.report?.responseId !== responseId) return null;

    const parsed = isApiSuccess(resp) ? trendDetailSchema.safeParse(resp.data) : null;
    if (parsed?.success) {
      dispatch(setTrendDetail({ key, loading: false, data: parsed.data }));
      return parsed.data;
    }
    if (parsed && !parsed.success) {
      captureMessage('Cultural Intel trend detail failed schema validation', 'error', {
        culturalRequestId: id,
        trendId,
        issues: parsed.error.issues,
      });
    }

    const notFound = httpStatusOf(resp) === 404;
    dispatch(
      setTrendDetail({
        key,
        loading: false,
        notFound,
        error: notFound
          ? 'This trend is no longer in the report.'
          : messageOf(resp, "We couldn't load this trend's details."),
      })
    );
    if (notFound) dispatch(fetchCulturalIntelligenceReport({ id, force: true, silent: true }));
    return null;
  });
};

/**
 * POST /cultural-intel/{id}/retry: run a failed first run again. The intel report's cultural layer
 * goes back to generating (as its `intel-report.{id}` push would say), and Reverb follows it from there.
 *
 * @param {{ id: number, intelReportId: string|number }} args
 * @returns {Promise<{ ok: boolean, error?: string }>}
 */
export const retryCulturalIntel = ({ id, intelReportId }) => (dispatch) =>
  retryCulturalIntelReport(id).then((resp) => {
    if (!isApiSuccess(resp)) {
      return { ok: false, error: messageOf(resp, "This report couldn't be retried.") };
    }
    dispatch(setStatus(null));
    dispatch(setReportPending(true));
    dispatch(
      reportLayerStatusChanged({
        intelReportId,
        culturalReport: { id, status: resp.data?.status ?? CULTURAL_STATUS.QUEUED },
      })
    );
    return { ok: true };
  });

// The API client cancels a request still in flight to the same endpoint, and every interpretation
// POSTs to the same one; so the runs go out one after another instead of cancelling each other.
let interpretQueue = Promise.resolve();

/**
 * "Interpret for my industry" on one trend of the edition on screen. The card shows the run at once;
 * 201 (new) and 200 (it already had one) both put the returned `{ id, status }` on it. A failure puts
 * the card back and keeps the reason for the drawer (429: more than 10 a minute).
 *
 * @param {{ id: number, trendId: string }} args
 */
export const requestTrendInterpretation = ({ id, trendId }) => (dispatch, getState) => {
  const { report, interpretations } = getState().CulturalIntelligence;
  if (!report) return Promise.resolve(null);
  const previous = interpretations[trendId] ?? null;

  dispatch(setInterpretError({ trendId, error: null }));
  dispatch(
    setInterpretation({
      trendId,
      interpretation: { id: previous?.id ?? null, status: CULTURAL_STATUS.QUEUED },
    })
  );

  const run = interpretQueue.then(() =>
    postTrendInterpretation(id, { culturalIntelResponseId: report.responseId, trendId })
  );
  interpretQueue = run.catch(() => null);

  return run.then((resp) => {
    const parsed = isApiSuccess(resp) ? interpretationSchema.safeParse(resp.data) : null;
    if (parsed?.success) {
      dispatch(
        setInterpretation({
          trendId,
          interpretation: { id: parsed.data.id, status: parsed.data.status },
        })
      );
      return parsed.data;
    }
    dispatch(setInterpretation({ trendId, interpretation: previous }));
    dispatch(
      setInterpretError({
        trendId,
        error:
          httpStatusOf(resp) === 429
            ? 'You’ve run a lot of interpretations in the last minute. Try again shortly.'
            : messageOf(resp, "We couldn't start this interpretation. Please try again."),
      })
    );
    return null;
  });
};

/**
 * GET .../interpretations/{interpretationId}: the content of a completed run, or why it failed.
 * Fetched once per finished run (a status push clears it).
 *
 * @param {{ id: number, interpretationId: number }} args
 */
export const fetchTrendInterpretation = ({ id, interpretationId }) => (dispatch, getState) => {
  const entry = getState().CulturalIntelligence.interpretationContent[interpretationId];
  if (entry?.loading || entry?.data) return Promise.resolve(entry?.data ?? null);

  dispatch(setInterpretationContent({ interpretationId, loading: true, error: null }));
  return getTrendInterpretation(id, interpretationId).then((resp) => {
    const parsed = isApiSuccess(resp) ? interpretationSchema.safeParse(resp.data) : null;
    if (parsed?.success) {
      // Still running: nothing to keep yet; the next status push fetches it again.
      const finished = !isInterpretationRunning(parsed.data);
      dispatch(
        setInterpretationContent({
          interpretationId,
          loading: false,
          data: finished ? parsed.data : null,
        })
      );
      // The card follows what the endpoint says (e.g. a push that never arrived).
      dispatch(
        setInterpretation({
          trendId: parsed.data.trendId,
          interpretation: { id: parsed.data.id, status: parsed.data.status },
        })
      );
      return parsed.data;
    }
    if (parsed && !parsed.success) {
      captureMessage('Cultural Intel interpretation failed schema validation', 'error', {
        interpretationId,
        issues: parsed.error.issues,
      });
    }
    dispatch(
      setInterpretationContent({
        interpretationId,
        loading: false,
        error: messageOf(resp, "We couldn't load this interpretation."),
      })
    );
    return null;
  });
};

export { initialState as culturalIntelligenceInitialState };

export default CulturalIntelligenceSlice.reducer;
